import { redirect } from "@tanstack/react-router";
import { authClient } from "./auth-client";
import { canViewAll } from "./permissions";

export async function requireAuth() {
  const session = await authClient.getSession();
  if (!session.data) throw redirect({ to: "/login" });
  return session.data;
}

/**
 * The bare domain (`/`): straight to the dashboard when already signed in,
 * otherwise to sign-in.
 *
 * It used to redirect to sign-in unconditionally, so anyone who opened the
 * bookmarked root of the app was shown the sign-in form while holding a
 * perfectly good session — which reads as having been signed out, and is worse
 * now that Microsoft sign-in makes a re-auth look like a second prompt for
 * credentials.
 */
export async function redirectFromRoot() {
  const session = await authClient.getSession();
  throw redirect({ to: session.data ? "/dashboard" : "/login" });
}

/**
 * Page guard for the admin-only routes (Settings, Org Health, PTO report, …).
 *
 * Deliberately the VIEW tier, not the edit tier: a read-only admin must be able
 * to reach these pages — seeing everything is the point of that role. The edit
 * affordances inside each page are gated separately with `canEditAll`, so
 * widening this guard cannot grant a write.
 */
export async function requireViewAll() {
  const session = await requireAuth();
  if (!canViewAll(session.user.role)) throw redirect({ to: "/dashboard" });
  return session;
}

/**
 * Page guard for the competency report: HR/admin, or a pillar head (EPIC-076
 * WI-284). The server's scope endpoint is the arbiter, so a head is let in
 * exactly when it would answer them.
 */
export async function requireReportAccess() {
  const session = await requireAuth();
  const allowed = await fetch("/api/reports/role-competency/scope")
    .then((res) => res.ok)
    .catch(() => false);
  if (!allowed) throw redirect({ to: "/dashboard" });
  return session;
}

/**
 * Page guard for the Reviews section, which is hidden until the January 2027
 * rollout.
 *
 * Deliberately a runtime check against `app_settings.reviews_enabled` and NOT a
 * `NODE_ENV` compile-out: `build.ts` defines `NODE_ENV` as `"production"` for
 * every bundle it produces, including the sandbox artifact testers use, so a
 * bundle-time gate would hide the section from the only people who need to see
 * it before launch. See `docs/performance-reviews-design.md`.
 *
 * This is convenience, not a boundary — the server answers 404 on every
 * `/api/reviews*` route when the flag is off, and that is the check that counts.
 * A failed fetch redirects rather than rendering an empty section.
 */
export async function requireReviews() {
  const session = await requireAuth();
  const enabled = await fetch("/api/app-settings")
    .then((res) => (res.ok ? res.json() : null))
    .then((json) => json?.data?.reviewsEnabled === true)
    .catch(() => false);
  if (!enabled) throw redirect({ to: "/dashboard" });
  return session;
}
